import { images } from '@/asset';
import BannerLimit from '@/components/BannerLimit';
import BaseModal from '@/components/BaseModal';
import BaseTable from '@/components/BaseTable';
import BoldText from '@/components/BoldText';
import { OrderUnfulfilled } from '@/components/Icons';
import ImportCSV from '@/components/ImportCSV';
import Layout from '@/components/layout';
import ModalConditionSummary from '@/components/ModalConditionSummary';
import { config } from '@/config';
import { BREAKPOINT, DisplayModeEnum, LocationConditionEnum, Touchpoint } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { checkShowErrorInline, openCrisp } from '@/helpers';
import { preloadImage } from '@/helpers/file';
import { stringify } from '@/helpers/string';
import { useCustomUserIdClarity } from '@/hooks/useCustomUserIdClarity';
import useToast from '@/hooks/useToast';
import useUserPlan from '@/hooks/useUserPlan';
import FilterRule from '@/pages/Settings/Components/EstimateRules/components/FilterRule';
import IconHasTooltip from '@/pages/Settings/Components/EstimateRules/components/IconHasTooltip';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import filterRulesSlice, { IRuleParamFilterKeys, paginationSelector } from '@/redux/slice/filterRules.slice';
import ruleSlice from '@/redux/slice/rule.slice';
import rulesSlice from '@/redux/slice/rules.slice';
import sessionSlice, { isShowBannerSelector } from '@/redux/slice/session.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IPagination } from '@/types';
import { IResponseApi } from '@/types/apis/response';
import { IItemCondition } from '@/types/newSetup.type';
import {
  Badge,
  Box,
  Button,
  Card,
  Icon,
  IndexTable,
  InlineStack,
  List,
  Text,
  Tooltip,
  useIndexResourceState,
} from '@shopify/polaris';
import {
  CalendarTimeIcon,
  CartIcon,
  DeleteIcon,
  DuplicateIcon,
  EditIcon,
  EmailIcon,
  GlobeIcon,
  ProductIcon,
} from '@shopify/polaris-icons';
import debounce from 'lodash/debounce';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { EstimateRulesStyled } from './styled';

const EstimateRules = () => {
  useCustomUserIdClarity('Rule setup');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { show } = useToast();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.XL });

  const [deleteMultipleRule, { isLoading: isLoadingDeleteMultiple }] = apiCaller.useBulkRemoveRuleMutation();
  const [updateMultipleRule, { isLoading: isLoadingUpdateMultiple }] = apiCaller.useBulkUpdateRuleMutation();
  const [createRule, { isLoading: isLoadingDuplicate }] = apiCaller.useCreateRuleMutation();

  const isSkip = useSelector(isSkipApiSelector);
  const params = useSelector(paginationSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const isVisibleBannerLimit = useSelector(isShowBannerSelector);

  const { limitPlan, isFreePlan } = useUserPlan();
  const { data: totalRule } = apiCaller.useRuleCountQuery(undefined, { skip: isSkip });
  const [deleteRule, { isLoading: isLoadingDelete }] = apiCaller.useRemoveRuleMutation();
  const [importRules, { isLoading: isLoadingImport }] = apiCaller.useImportRulesMutation();
  const { data, isFetching: isFetchingRules } = apiCaller.useGetRulesQuery(
    {
      ...params,
      states: params.states?.map((state: string) => state.split('-')[1]),
      sortBy: dataSettings.ruleSortBy,
    },
    { skip: isSkip },
  );

  const [isShowModalDelete, setIsShowModalDelete] = useState(false);
  const [selectedId, setSelectedId] = useState<number | undefined>();
  const [inputSearch, setInputSearch] = useState(params?.name || '');
  const [isShowModalSummarize, setIsShowModalSummarize] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<number>();

  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } = useIndexResourceState(
    data?.data ? (data?.data?.map((data) => ({ ...data, id: data?.id ? data?.id.toString() : '' })) as any) : [],
  );

  const isDisableCreate = useMemo(() => {
    return limitPlan && limitPlan.maxRule <= (totalRule?.data?.countRule || 0);
  }, [limitPlan, totalRule?.data?.countRule]);

  useEffect(() => {
    dispatch(rulesSlice.actions.saveAllRules(data?.data || []));
  }, [data?.data, dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleUpdateSearch = useCallback(
    debounce((value: string) => {
      dispatch(
        filterRulesSlice.actions.updatePagination({
          page: 1,
          name: value,
        }),
      );
    }, 300),
    [dispatch, params],
  );

  const handleShowModalSummarize = (id: number) => {
    setSelectedMethod(id);
    setIsShowModalSummarize(true);
  };

  const handleBulkUpdate = useCallback(
    async (key: 'enable' | 'showTextInCart', value: boolean) => {
      const data = { ids: selectedResources.map(Number), [key]: value };
      const res = await updateMultipleRule(data).unwrap();
      if (res.code === 200) {
        show('Rules updated');
      }
    },
    [selectedResources, updateMultipleRule, show],
  );

  const actionItems = useMemo(() => {
    return [
      {
        content: 'Show in cart',
        onAction: () => {
          handleBulkUpdate('showTextInCart', true);
        },
        loading: isLoadingUpdateMultiple || isLoadingDeleteMultiple,
        disabled: isFreePlan,
      },
      {
        content: 'Hide in cart',
        onAction: () => {
          handleBulkUpdate('showTextInCart', false);
        },
        loading: isLoadingUpdateMultiple || isLoadingDeleteMultiple,
        disabled: isFreePlan,
      },
      {
        content: 'Activate',
        onAction: () => {
          handleBulkUpdate('enable', true);
        },
        loading: isLoadingUpdateMultiple || isLoadingDeleteMultiple,
      },
      {
        content: 'Deactivate',
        onAction: () => {
          handleBulkUpdate('enable', false);
        },
        loading: isLoadingUpdateMultiple || isLoadingDeleteMultiple,
      },
      {
        content: <Icon source={DeleteIcon} />,
        onAction: () => {
          setSelectedId(-1);
          setIsShowModalDelete(true);
        },
        loading: isLoadingDeleteMultiple || isLoadingUpdateMultiple,
      },
    ];
  }, [handleBulkUpdate, isFreePlan, isLoadingDeleteMultiple, isLoadingUpdateMultiple]);

  const handleDelete = useCallback(async () => {
    const isDeleteMultiple = selectedResources.length > 0 && selectedId === -1;
    if (!selectedId && !isDeleteMultiple) return;
    let res: IResponseApi.ICommon;
    if (isDeleteMultiple) {
      res = await deleteMultipleRule(selectedResources.join(',')).unwrap();
    } else {
      res = await deleteRule(+selectedId).unwrap();
    }
    if (res.status === 'success') {
      show(isDeleteMultiple ? 'Rules deleted' : 'Rule deleted');
    }

    setIsShowModalDelete(false);
    clearSelection();
  }, [selectedResources, selectedId, clearSelection, deleteMultipleRule, deleteRule, show]);

  const handleImportArea = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('shop', config.shop || '');
    formData.append('urlParams', stringify(config.urlParams) || '');
    const res = await importRules(formData);
    return res;
  };

  const handleDuplicateRule = useCallback(
    async (currentId: number) => {
      const currentRule = data?.data?.find((rule) => rule.id === currentId);
      //@ts-expect-error
      const { id, itemConditions, ...rest } = currentRule;
      if (!currentRule) return;
      // const listConditionMetafieldKeys = [
      //   ItemConditionEnum.ExcludeProductMetafield,
      //   ItemConditionEnum.IncludeProductMetafield,
      //   ItemConditionEnum.ExcludeVariantMetafield,
      //   ItemConditionEnum.IncludeVariantMetafield,
      // ];
      const handleRemoveEmptyData = (conditions: IItemCondition[]) => {
        let res = conditions.map((condition) => {
          // if (listConditionMetafieldKeys.includes(condition.type as ItemConditionEnum)) {
          //   return {
          //     ...condition,
          //     value: condition.value?.map((value) => ({ ...value, data: JSON.stringify(value?.data) })),
          //   };
          // }
          return {
            ...condition,
            value: condition.value?.filter((value) => value?.data),
          };
        });

        res = res.filter((item) => item.value.length);

        return res;
      };

      let newItemConditions = handleRemoveEmptyData(itemConditions) as IItemCondition[];
      try {
        const res = await createRule({
          ...rest,
          itemConditions: newItemConditions,
          name: `Copy of ${currentRule.name}`,
          styledName: `Copy of ${currentRule.styledName}`,
        }).unwrap();
        const checkError = checkShowErrorInline(res);
        if (!checkError.status) {
          show(`Rule ${id ? 'updated' : 'created'}`);
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    [data?.data, createRule, show],
  );

  const rowMarkup = useMemo(() => {
    return data?.data?.map(
      (
        {
          id,
          name,
          enable,
          soldOutEnabled,
          locationConditions,
          maxDeliveryDays,
          maxPrepDays,
          minDeliveryDays,
          minPrepDays,
          showTextInCart,
          displayMode,
        },
        index,
      ) => {
        const currentLocationConditions = locationConditions?.find(
          (condition) => condition.type === LocationConditionEnum.Country,
        );
        const currentZipcodeConditions = locationConditions?.find(
          (condition) => condition.type === LocationConditionEnum.Zipcode,
        );
        const isHasCountryConditions = currentLocationConditions && currentLocationConditions.value.length > 0;
        const isHasZipcodeConditions = currentZipcodeConditions && currentZipcodeConditions.value.length > 0;

        return (
          <IndexTable.Row
            id={id + ''}
            key={id}
            selected={selectedResources.includes(id + '')}
            position={index}
            onClick={() => {}} //empty fn to prevent select checkbox when click row
          >
            <IndexTable.Cell>
              <Text variant="bodyMd" fontWeight="bold" as="span">
                {name}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Tooltip
                content={
                  <List>
                    <List.Item>
                      Minimum preparation days: <BoldText>{minPrepDays}</BoldText>
                    </List.Item>
                    <List.Item>
                      Maximum preparation days: <BoldText>{maxPrepDays}</BoldText>
                    </List.Item>
                    <List.Item>
                      Minimum delivery days: <BoldText>{minDeliveryDays}</BoldText>
                    </List.Item>
                    <List.Item>
                      Maximum delivery days: <BoldText>{maxDeliveryDays}</BoldText>
                    </List.Item>
                  </List>
                }
              >
                <div
                  key={id}
                  className="estimate-table-data"
                >{`${minPrepDays}-${maxPrepDays}-${minDeliveryDays}-${maxDeliveryDays}`}</div>
              </Tooltip>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <InlineStack>
                <IconHasTooltip
                  icon={ProductIcon}
                  tooltipContent={'View display conditions of this rule'}
                  onClick={() => handleShowModalSummarize(id as number)}
                />
                {isHasCountryConditions ? (
                  <IconHasTooltip
                    icon={GlobeIcon}
                    tooltipContent={'View display conditions of this rule'}
                    onClick={() => handleShowModalSummarize(id as number)}
                  />
                ) : null}
                {isHasZipcodeConditions ? (
                  <IconHasTooltip
                    icon={EmailIcon}
                    tooltipContent={'View display conditions of this rule'}
                    onClick={() => handleShowModalSummarize(id as number)}
                  />
                ) : null}
                {displayMode !== DisplayModeEnum.AllTime ? (
                  <IconHasTooltip
                    icon={CalendarTimeIcon}
                    tooltipContent="Have customized display time"
                    onClick={() => handleShowModalSummarize(id as number)}
                  />
                ) : null}
              </InlineStack>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <InlineStack>
                {showTextInCart ? <IconHasTooltip icon={CartIcon} tooltipContent={'Show in cart'} /> : null}
                {soldOutEnabled ? (
                  <IconHasTooltip
                    iconElement={<OrderUnfulfilled fill="#8a8a8a" />}
                    tooltipContent={'Show when items are sold-out'}
                  />
                ) : null}
              </InlineStack>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <div
                onClick={() => {
                  handleSelectionChange('single' as any, !selectedResources.includes(id + ''), id + '');
                }}
                className="d-inline-block cursor-pointer"
              >
                <Badge tone={enable ? 'success' : undefined}>{enable ? 'Active' : 'Inactive'}</Badge>
              </div>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <div className="flex-center gap-8">
                <Tooltip content="Edit">
                  <Button
                    disabled={isLoadingDuplicate}
                    icon={EditIcon}
                    onClick={() => {
                      navigate({
                        pathname: PATH.EDIT_RULE.pathname.replace(':id', (id || '').toString()),
                        search: PATH.EDIT_RULE.search,
                      });
                    }}
                    variant="plain"
                  />
                </Tooltip>
                <Tooltip content="Duplicate">
                  <Button
                    disabled={isLoadingDuplicate || isDisableCreate}
                    icon={DuplicateIcon}
                    onClick={() => {
                      handleDuplicateRule(id as number);
                    }}
                    variant="plain"
                  />
                </Tooltip>
                <Tooltip content="Delete">
                  <Button
                    disabled={isLoadingDuplicate}
                    icon={DeleteIcon}
                    onClick={() => {
                      setSelectedId(id);
                      setIsShowModalDelete(true);
                    }}
                    variant="plain"
                  />
                </Tooltip>
              </div>
            </IndexTable.Cell>
          </IndexTable.Row>
        );
      },
    );
  }, [data?.data, handleDuplicateRule, handleSelectionChange, isDisableCreate, isLoadingDuplicate, navigate, selectedResources]);

  const isSearching = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, perPage, ...rest } = params;
    return Object.keys(rest).some((key) => rest[key as keyof IRuleParamFilterKeys]);
  }, [params]);

  useEffect(() => {
    preloadImage(images.empty_country);
  }, []);

  return (
    <Layout
      noPaddingInButton
      title="Estimate rules"
      subtitle="Display condition-based estimated delivery dates on storefront (product, collection, and cart page)"
      fullWidth={false}
      primaryAction={{
        content: 'Create rule',
        onAction: () => {
          navigate(PATH.CREATE_RULE);
          dispatch(ruleSlice.actions.handleInit());
        },
        disabled: isDisableCreate,
      }}
      secondaryActions={[
        {
          content: (
            <ImportCSV
              title={'Import rules'}
              importing={isLoadingImport}
              onImport={handleImportArea}
              buttonLabel="Import rules"
            />
          ) as any,
        },
      ]}
    >
      <EstimateRulesStyled isMobile={isMobile}>
        <BannerLimit
          touchpoint={Touchpoint.SettingsTopBanner}
          isVisible={isVisibleBannerLimit.isVisibleBannerLimitInRuleConfig}
          onDismiss={() => {
            dispatch(sessionSlice.actions.handleBanner({ key: 'isVisibleBannerLimitInRuleConfig', value: false }));
          }}
        />
        <div className="mt-12">
          <Card padding="0">
            <FilterRule
              onSearchChange={(value) => {
                setInputSearch(value);
                handleUpdateSearch(value);
              }}
              searchValue={inputSearch}
            />

            <BaseTable
              isNotUsingCard
              isLoading={isFetchingRules}
              isFetching={isFetchingRules}
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
              headings={[
                { title: 'Rule name' },
                { title: 'Estimate' },
                { title: 'Conditions' },
                { title: 'Advanced' },
                { title: 'Status' },
                { title: 'Actions', alignment: 'center' },
              ]}
              data={data?.data || []}
              rows={rowMarkup || []}
              actionItems={actionItems}
              totalPage={data?.meta?.pagination?.total_pages || 0}
              totalItem={data?.meta?.pagination?.total || 0}
              pagination={{ page: params.page, perPage: params.perPage }}
              setPagination={(pagination: IPagination) => {
                dispatch(
                  filterRulesSlice.actions.updatePagination({
                    ...pagination,
                  }),
                );
              }}
              selectedResources={selectedResources}
              allResourcesSelected={allResourcesSelected}
              handleSelectionChange={handleSelectionChange}
              imgEmptySource={images.empty_country}
              titleEmpty={'No rule created'}
              emptyAction={() => {
                if (isSearching) {
                  openCrisp();
                } else if (!isDisableCreate) {
                  navigate(PATH.CREATE_RULE);
                  dispatch(ruleSlice.actions.handleInit());
                }
              }}
              titleActionBtnEmpty={isSearching ? 'Contact support' : 'New rule'}
              isSearching={isSearching}
            />
          </Card>
        </div>
        <ModalConditionSummary
          id={selectedMethod as number}
          onEdit={() => {
            navigate(
              {
                pathname: PATH.EDIT_RULE.pathname.replace(':id', (selectedMethod || '').toString()),
                search: PATH.EDIT_RULE.search,
              },
              { state: { isDefaultSelectDisplayConditionTab: true } },
            );
          }}
          isOpenModal={isShowModalSummarize}
          setIsOpenModal={setIsShowModalSummarize}
        />
        <BaseModal
          title={'Delete this delivery method rule?'}
          open={isShowModalDelete}
          onClose={() => setIsShowModalDelete(false)}
          primaryAction={{
            content: 'Delete',
            onAction: handleDelete,
            loading: isLoadingDelete || isLoadingDeleteMultiple,
            id: 'btn-destructive-tone',
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setIsShowModalDelete(false),
            },
          ]}
          children={
            <>
              <Box padding="200">Delete selected rule.</Box>
            </>
          }
        />
      </EstimateRulesStyled>
    </Layout>
  );
};

export default memo(EstimateRules);
